/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react"
import NumberFormat from "react-number-format"
import {Checkbox, Col, Divider, Radio, Row, Spin} from "antd"
import {useTranslation} from "react-i18next"
import cx from "classnames"

import {BaseButton, BaseInput} from "components"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import {
    paymentCurrencyOptions,
    PaymentProvider,
    PaymentType,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"
import {AuthorizeNetPayment, StripePayment} from "./parts"
import {formatCurrency, getFullName, handleError, toastError} from "helpers"
import {generalSettingsService, studentAccountService, studentPaymentService} from "services"
import styles from "./PaymentModal.module.css"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {Order} from "types/common"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {useDepartmentStudent, useLabels, useModel} from "hooks"
import {preventMinusCharacter} from "helpers/inputHelper"
import {StudentCharge} from "types/student-account/student-ledger"
import {sum} from "lodash"
import moment from "moment"
import {PaymentPlanItem, PaymentPlanLinkedEntity} from "types/student-account/payment-plan"
import PaymentPlanItemsTable from "sections/StudentAccount/Payment/PaymentModal/parts/PaymentPlanItemsTable"
import {SquarePayment} from "./parts/SquarePayment"
import {useQuery} from "@tanstack/react-query"
import {GeneralSettingField} from "types/settings/general-setting"
import {Settings} from "types/settings"
import {Label} from "components/inputs/Label"
import {DirectEmbedPayment} from "./parts/DirectEmbedPayment"
import {PaymentMethod} from "./parts/DirectEmbedPayment/DirectEmbedPayment"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {StudentPaymentContext} from "context/StudentPaymentContext"
import {PayPalPayment} from "./parts/Paypal/Paypal"
import {BaseDepartmentId} from "types/departments"

const currentYear = moment().year()
const yearOptions = [1, 0, -1, -2].map((index) => ({
    value: currentYear + index,
    label: `${currentYear + index} - ${currentYear + index + 1}`
}))

const TURNAGE_GROUP_SLUG = "turnagegroup"
const TURNAGE_GROUP_FEE_PERCENT = 4.5

type PaymentViewProps = {
    studentId: number
    planItemIds?: number[]
    hideViewTitle?: boolean
}

export const PaymentView: React.FC<PaymentViewProps> = ({studentId, planItemIds, hideViewTitle = false}) => {
    const {t} = useTranslation(["studentAccount", "common"])
    const labels = useLabels()
    const {paymentStatus, paymentError} = useContext(StudentPaymentContext)

    const {
        paymentProviderConfigs: [providerConfig]
    } = usePaymentProviderConfigs()

    const [academicYear, setAcademicYear] = useState<number | undefined>()
    const [paymentType, setPaymentType] = useState(PaymentType.Item)
    const [paymentAmount, setPaymentAmount] = useState(0)
    const [currency, setCurrency] = useState<StudentPaymentSupportedCurrency>("usd")
    const [currencyMinAmount, setCurrencyMinAmount] = useState<Record<string, number>>({})
    const [generalPayments, setGeneralPayments] = useState<GeneralPayment[]>([])
    const [selectedPayment, setSelectedPayment] = useState<GeneralPayment | undefined>()
    const [customNote, setCustomNote] = useState("")
    const [isCheckingOut, setCheckingOut] = useState(false)
    const [isPaymentRecurring, setPaymentRecurring] = useState(false)
    const [paymentPlanItems, setPaymentPlanItems] = React.useState<PaymentPlanItem[]>([])
    const [selectedPaymentItems, setSelectedPaymentItems] = React.useState<PaymentPlanItem[]>([])
    const [charges, setCharges] = React.useState<StudentCharge[]>([])
    const [selectedCharges, setSelectedCharges] = React.useState<StudentCharge[]>([])
    const [directEmbedMethodConfig, setDirectEmbedMethodConfig] = useState<{
        paymentMethod: PaymentMethod
    }>({
        paymentMethod: PaymentMethod.CARD
    })
    const [openDirectEmbedModal, setOpenDirectEmbedModal] = useState<boolean>(false)

    const settingsQuery = useQuery(
        ["sa-hide-general-payment"],
        async () => {
            return await generalSettingsService.getSettings([GeneralSettingField.SaHideGeneralPayment])
        },
        {
            initialData: []
        }
    )

    const hideGeneralPayment = !!JSON.parse(
        settingsQuery.data?.find((setting) => setting.field === GeneralSettingField.SaHideGeneralPayment)?.value ?? null
    )

    useEffect(() => setPaymentAmount(sum(selectedCharges.map((charge) => charge.paymentAmount))), [selectedCharges])

    useEffect(
        () => setPaymentAmount(sum(selectedPaymentItems.map((item) => item.paymentAmount))),
        [selectedPaymentItems]
    )

    useEffect(() => {
        ;(async function getGeneralPayments() {
            try {
                const {data: generalPayments} = await studentAccountService.getGenralPayments({
                    filter: {isActive: true},
                    range: {
                        page: 1,
                        pageSize: UNLIMITED_PAGE_SIZE
                    },
                    sort: {
                        orderBy: "position",
                        orderDir: Order.Asc
                    }
                })
                setGeneralPayments(generalPayments)
            } catch (error) {
                console.error(error)
            }
        })()
    }, [])

    useEffect(() => {
        if (paymentType !== PaymentType.Item) return
        ;(async function loadPaymentPlanItems() {
            try {
                const {data} = await studentAccountService.getPaymentPlans({
                    filter: {
                        profileIds: [studentId]
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    },
                    linkedEntities: [PaymentPlanLinkedEntity.Charges, PaymentPlanLinkedEntity.Items]
                })
                const planItems = data.flatMap((paymentPlan) =>
                    paymentPlan.items.map((item) => ({...item, paymentPlan}))
                )
                setPaymentPlanItems(planItems)
                console.log("Selected plan item IDs:", planItemIds, planItems)
                if (planItemIds?.length) {
                    setSelectedPaymentItems(
                        planItems
                            .filter((item) => planItemIds.includes(item.paymentPlanItemId))
                            .map((item) => ({
                                ...item,
                                isChecked: true,
                                isActiveTableCol: true,
                                paymentAmount: item.amount - item.paidAmount,
                                paymentAccountId: item.paymentAccountId ?? item.paymentPlan?.accountId
                            }))
                    )
                }
            } catch (error) {
                console.error(error)
                setPaymentPlanItems([])
            }
        })()
    }, [studentId, academicYear, planItemIds, paymentType])

    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const {student} = useDepartmentStudent({studentId, departmentId: BaseDepartmentId.StudentAccount})

    const payerFullName = useMemo(() => getFullName(student), [student])
    const normalizedPaymentAmount = Number(paymentAmount) || 0
    const shouldShowTurnageFee =
        providerConfig?.provider === PaymentProvider.DirectEmbed && model.slug === TURNAGE_GROUP_SLUG
    const turnageFeeAmount = shouldShowTurnageFee ? (normalizedPaymentAmount * TURNAGE_GROUP_FEE_PERCENT) / 100 : 0
    const paymentButtonAmount = shouldShowTurnageFee
        ? normalizedPaymentAmount + turnageFeeAmount
        : normalizedPaymentAmount

    const paymentTypeOptions = useMemo(
        () =>
            hideGeneralPayment
                ? [{value: PaymentType.Item, label: "Select Payment Item"}]
                : [
                      {value: PaymentType.General, label: "General Payment"},
                      {value: PaymentType.Item, label: "Select Payment Item"}
                  ],
        [hideGeneralPayment]
    )

    const resetPaymentForm = useCallback(() => {
        setPaymentType(PaymentType.Item)
        setCurrency("usd")
        setPaymentAmount(0)
        setSelectedCharges([])
        setSelectedPaymentItems([])
        setSelectedPayment(generalPayments[0])
        setCustomNote("")
        setAcademicYear(undefined)
        setCheckingOut(false)
        setPaymentRecurring(false)
        setOpenDirectEmbedModal(false)
        setDirectEmbedMethodConfig({paymentMethod: PaymentMethod.CARD})
    }, [generalPayments])

    useEffect(() => {
        if (paymentStatus === "confirmed" && !paymentError) {
            resetPaymentForm()
        }
    }, [paymentStatus, paymentError, resetPaymentForm])

    const renderPaymentContentTitle = () => {
        if (paymentType === PaymentType.Item) {
            return (
                <>
                    <div>Total Amount</div>
                    <div className={styles.textGraySmall}>{t("payment.specifyPaymentAmount")}</div>
                </>
            )
        }
        return (
            <>
                <div>Specify amount</div>
                <div className={styles.textGraySmall}>Enter amount you would like to pay</div>
            </>
        )
    }

    const renderPaymentContent = () => {
        return (
            <Col span={24}>
                <Row gutter={16} align="middle" wrap={false}>
                    <Col flex={1}>{renderPaymentContentTitle()}</Col>
                    <Col flex="100px">
                        <KlassDropdown
                            readOnly={isCheckingOut}
                            placeholder={t("payment.currency")}
                            options={paymentCurrencyOptions}
                            onChange={(option) => setCurrency(option.id)}
                            value={paymentCurrencyOptions.find((option) => option.id === currency)}
                        />
                    </Col>
                    <Col flex="100px">
                        <NumberFormat
                            readOnly={isCheckingOut}
                            thousandSeparator
                            className={styles.input}
                            value={paymentAmount}
                            min={0}
                            decimalScale={2}
                            onValueChange={({floatValue}) => setPaymentAmount(floatValue)}
                            onKeyDown={preventMinusCharacter}
                        />
                    </Col>
                </Row>
                {shouldShowTurnageFee && normalizedPaymentAmount > 0 ? (
                    <div className={cx(styles.textGraySmall, "mt-8")}>
                        Fee {TURNAGE_GROUP_FEE_PERCENT}% = {formatCurrency(turnageFeeAmount, currency)}
                    </div>
                ) : null}
            </Col>
        )
    }

    const renderPaymentItemContent = () => {
        return (
            <div className="my-24">
                <h4 className={cx(styles.sectionTitle, "mb-24")}>{t("payment.selectPaymentItem")}</h4>

                <PaymentPlanItemsTable
                    {...{
                        paymentPlanItems,
                        selectedPaymentItems,
                        setSelectedPaymentItems
                    }}
                />
            </div>
        )
    }

    const renderGeneralPaymentContent = () => {
        return (
            <div className="my-24">
                <Row gutter={[16, 16]} className={styles.highlightBlock}>
                    <Col span={24}>
                        <h4 className={cx(styles.sectionTitle, "my-0")}>MAKE GENERAL PAYMENT</h4>
                    </Col>

                    {renderPaymentContent()}
                    <Col span={24}>
                        <div className={styles.textGraySmall}>Pay for</div>
                        <KlassDropdown
                            readOnly={isCheckingOut}
                            placeholder={t("payment.note")}
                            options={generalPayments}
                            valueKey="generalPaymentId"
                            labelKey="name"
                            value={selectedPayment}
                            onChange={setSelectedPayment}
                        />
                    </Col>

                    <Col span={24}>
                        <div className={styles.textGraySmall}>Add your notes</div>
                        <BaseInput
                            value={customNote}
                            onChange={setCustomNote}
                            placeholder="Type your notes"
                            readOnly={isCheckingOut}
                        />
                    </Col>
                </Row>
            </div>
        )
    }

    return (
        <>
            <div className={styles.popupWrapper}>
                {!hideViewTitle && (
                    <>
                        <h3 className={styles.modalTitle}>{t("payment.makeAPayment")}</h3>
                        <Divider className="mt-10 mb-0" />
                    </>
                )}

                <Row className={cx(styles.modalContent, {[styles.spanFull]: isCheckingOut})}>
                    <Col span={isCheckingOut ? 12 : 24} className={styles.modalLeftSide}>
                        <Radio.Group
                            disabled={isCheckingOut}
                            options={paymentTypeOptions}
                            value={paymentType}
                            onChange={({target: {value}}) => {
                                setPaymentType(value)
                                setCurrency("usd")
                                setSelectedCharges([])
                                setSelectedPaymentItems([])
                                setSelectedPayment(undefined)
                                setCustomNote("")
                            }}
                            optionType="button"
                            buttonStyle="solid"
                            className={styles.radioGroup}
                        />

                        {/*
                        {labels.isActive(Settings.StudentAccountLabel.AcademicYear) && (
                            <div className="mt-24">
                                <FormLabel label={t("payments.academicYear")} />
                                <KlassDropdown
                                    isClearable
                                    options={yearOptions}
                                    valueKey="value"
                                    labelKey="label"
                                    placeholder="Select"
                                    value={yearOptions.find((option) => option.value === academicYear)}
                                    onChange={(option) => setAcademicYear(option?.value)}
                                />
                            </div>
                        )} 
                        */}

                        {paymentType === PaymentType.Item ? renderPaymentItemContent() : renderGeneralPaymentContent()}

                        {providerConfig?.provider === PaymentProvider.DirectEmbed && (
                            <div>
                                <Label text="Select Payment Method">
                                    <div className={styles.paymentMethodContainer}>
                                        <label className={styles.methodCard}>
                                            <Radio
                                                value={PaymentMethod.CARD}
                                                checked={directEmbedMethodConfig.paymentMethod === PaymentMethod.CARD}
                                                onChange={(e) =>
                                                    setDirectEmbedMethodConfig((prev) => ({
                                                        ...prev,
                                                        paymentMethod: e.target.value
                                                    }))
                                                }
                                            />
                                            <span>CREDIT CARD</span>
                                        </label>

                                        <label className={styles.methodCard}>
                                            <Radio
                                                value={PaymentMethod.ACH}
                                                checked={directEmbedMethodConfig.paymentMethod === PaymentMethod.ACH}
                                                onChange={(e) =>
                                                    setDirectEmbedMethodConfig((prev) => ({
                                                        ...prev,
                                                        paymentMethod: e.target.value
                                                    }))
                                                }
                                            />
                                            <span>ACH BANK PAYMENT</span>
                                        </label>
                                    </div>
                                </Label>
                                <div
                                    className={cx(styles.checkbox, "mt-24", {
                                        [styles.active]: isPaymentRecurring
                                    })}>
                                    <Checkbox
                                        checked={isPaymentRecurring}
                                        onChange={(event) => {
                                            setPaymentRecurring(event.target.checked)
                                        }}>
                                        Make Payment Recurring
                                    </Checkbox>
                                </div>
                            </div>
                        )}

                        <BaseButton
                            className="w-full mt-24"
                            variant={isCheckingOut ? "secondary" : "primary"}
                            disabled={!paymentAmount || paymentAmount <= 0}
                            title={t(isCheckingOut ? "payment.updatePayment" : "payment.payAmount", {
                                amount: formatCurrency(paymentButtonAmount, currency)
                            })}
                            onClick={() => {
                                if (Math.floor(paymentAmount * 100) < currencyMinAmount[currency]) {
                                    toastError(
                                        t("payment.minCurrencyAmountError", {
                                            min: formatCurrency(currencyMinAmount[currency] / 100, currency)
                                        })
                                    )
                                    return
                                }
                                if (providerConfig?.provider === PaymentProvider.DirectEmbed) {
                                    setOpenDirectEmbedModal((prev) => !prev)
                                } else setCheckingOut((prev) => !prev)
                            }}
                        />
                    </Col>

                    {isCheckingOut && (
                        <Col span={12} className={styles.modalRightSide}>
                            <h4 className={styles.sectionTitle}>{t("payment.methodOfPayment")}</h4>

                            <Row gutter={16}></Row>

                            {!providerConfig && (
                                <div>
                                    <Spin />
                                </div>
                            )}

                            {providerConfig?.provider === PaymentProvider.Stripe ? (
                                <StripePayment
                                    providerConfig={providerConfig}
                                    studentId={studentId}
                                    paymentAmount={paymentAmount}
                                    currency={currency}
                                    isCheckingOut={isCheckingOut}
                                    selectedPayment={selectedPayment}
                                    customNote={customNote}
                                    setCurrencyMinAmount={setCurrencyMinAmount}
                                    payerFullName={payerFullName}
                                    selectedPaymentItems={selectedPaymentItems}
                                    academicYear={academicYear}
                                />
                            ) : providerConfig?.provider === PaymentProvider.AuthorizeNet ? (
                                <AuthorizeNetPayment
                                    providerConfig={providerConfig}
                                    studentId={studentId}
                                    paymentAmount={paymentAmount}
                                    currency={currency}
                                    isCheckingOut={isCheckingOut}
                                    selectedPayment={selectedPayment}
                                    customNote={customNote}
                                    setCurrencyMinAmount={setCurrencyMinAmount}
                                    payerFullName={payerFullName}
                                    selectedPaymentItems={selectedPaymentItems}
                                    academicYear={academicYear}
                                />
                            ) : providerConfig?.provider === PaymentProvider.Square ? (
                                <SquarePayment
                                    providerConfig={providerConfig}
                                    studentId={studentId}
                                    paymentAmount={paymentAmount}
                                    currency={currency}
                                    isCheckingOut={isCheckingOut}
                                    selectedPayment={selectedPayment}
                                    customNote={customNote}
                                    setCurrencyMinAmount={setCurrencyMinAmount}
                                    payerFullName={payerFullName}
                                    selectedPaymentItems={selectedPaymentItems}
                                    academicYear={academicYear}
                                />
                            ) : providerConfig?.provider === PaymentProvider.Paypal ? (
                                <PayPalPayment
                                    providerConfig={providerConfig}
                                    studentId={studentId}
                                    paymentAmount={paymentAmount}
                                    currency={currency}
                                    isCheckingOut={isCheckingOut}
                                    selectedPayment={selectedPayment}
                                    customNote={customNote}
                                    setCurrencyMinAmount={setCurrencyMinAmount}
                                    payerFullName={payerFullName}
                                    selectedPaymentItems={selectedPaymentItems}
                                    academicYear={academicYear}
                                />
                            ) : null}
                        </Col>
                    )}
                </Row>
            </div>
            {providerConfig?.provider === PaymentProvider.DirectEmbed && openDirectEmbedModal ? (
                <DirectEmbedPayment
                    isOpen={openDirectEmbedModal}
                    setIsOpen={setOpenDirectEmbedModal}
                    method={directEmbedMethodConfig.paymentMethod}
                    publishedKey={providerConfig.publishedKey}
                    providerConfig={providerConfig}
                    studentId={studentId}
                    paymentAmount={paymentAmount}
                    currency={currency}
                    selectedPayment={selectedPayment}
                    customNote={customNote}
                    setCurrencyMinAmount={setCurrencyMinAmount}
                    payerFullName={payerFullName}
                    selectedPaymentItems={selectedPaymentItems}
                    academicYear={academicYear}
                    isPaymentRecurring={isPaymentRecurring}
                />
            ) : null}
        </>
    )
}
